import React, { useEffect, useState, useMemo } from "react";
import { fetchAnalytics, fetchMatters } from "../lib/api";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { todayISO, ndaysAgoISO } from "../lib/dates";

export default function LodestarTab() {
  const [range, setRange] = useState("30");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [projectId, setProjectId] = useState("");
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [analyticsData, setAnalyticsData] = useState(null);

  useEffect(() => {
    fetchMatters().then(setMatters).catch(console.error);
  }, []);

  function computeRange(){
    if (fromDate && toDate) return { from: fromDate, to: toDate };
    if (range === 'all') return { from: '1970-01-01', to: todayISO() };
    let days = parseInt(range,10);
    if (isNaN(days) || days < 1) days = 30;
    return { from: ndaysAgoISO(days-1), to: todayISO() };
  }

  async function loadAnalytics(){
    setLoading(true);
    setError(null);
    try {
      const { from, to } = computeRange();
      const data = await fetchAnalytics({ from, to, projectId: projectId || undefined });
      setAnalyticsData(data);
    } catch (e) {
      console.error(e);
      setError("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAnalytics(); }, [range, fromDate, toDate, projectId]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "—";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const totalLodestar = analyticsData?.totalLodestar || 0;
  const totalHours = analyticsData?.totalHours || 0;
  const userSummaries = analyticsData?.userSummaries || [];
  const matterSummaries = analyticsData?.matterSummaries || [];

  // Get matter names for display
  const matterMap = useMemo(() => {
    const map = {};
    matters.forEach(m => { map[m.id] = m.name; });
    return map;
  }, [matters]);

  function exportLodestarCSV(){
    const { from, to } = computeRange();
    const lines = [
      `Lodestar Report`,
      `Period: ${from} to ${to}`,
      ``,
      `User Breakdown`,
      `User,Email,Rate,Hours,Lodestar`,
      ...userSummaries.map(u => 
        `"${u.userName}","${u.userEmail}",${u.rate || ''},${u.totalHours.toFixed(1)},${u.lodestar ? u.lodestar.toFixed(2) : ''}`
      ),
      ``,
      `Matter Breakdown`,
      `Matter,Hours,Lodestar`,
      ...matterSummaries.map(m => 
        `"${m.projectName || matterMap[m.projectId] || m.projectId}",${m.totalHours.toFixed(1)},${m.lodestar ? m.lodestar.toFixed(2) : ''}`
      ),
      ``,
      `Totals`,
      `Total Hours,${totalHours.toFixed(1)}`,
      `Total Lodestar,${totalLodestar.toFixed(2)}`
    ];
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; 
    a.download = `lodestar_report_${from}_to_${to}.csv`; 
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6" data-testid="lodestar-tab">
      {/* Filters */}
      <div className="rounded border p-3">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Quick range</div>
            <Select value={range} onValueChange={(v)=>{ setRange(v); setFromDate(""); setToDate(""); }}>
              <SelectTrigger className="w-[160px]" data-testid="lodestar-range-select">
                <SelectValue placeholder="Range"/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Past Year</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">From</div>
            <input 
              type="date" 
              value={fromDate} 
              onChange={(e)=>setFromDate(e.target.value)} 
              className="border rounded px-2 py-1 text-sm"
              data-testid="lodestar-from-date"
            />
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">To</div>
            <input 
              type="date" 
              value={toDate} 
              onChange={(e)=>setToDate(e.target.value)} 
              className="border rounded px-2 py-1 text-sm"
              data-testid="lodestar-to-date"
            />
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Matter</div>
            <Select value={projectId || "all"} onValueChange={(v)=> setProjectId(v==='all'? '' : v)}>
              <SelectTrigger className="w-[240px]" data-testid="lodestar-matter-select">
                <SelectValue placeholder="All matters"/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All matters</SelectItem>
                {matters.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto">
            <Button variant="secondary" onClick={exportLodestarCSV} data-testid="export-lodestar-btn">
              Export Lodestar CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Loading/Error states */}
      {loading && <div className="text-center text-muted-foreground py-4">Loading analytics...</div>}
      {error && <div className="text-center text-red-500 py-4">{error}</div>}

      {/* Summary Cards */}
      {!loading && analyticsData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded border p-4 bg-card" data-testid="total-hours-card">
            <div className="text-sm text-muted-foreground">Total Hours</div>
            <div className="text-2xl font-bold tabular-nums">{totalHours.toFixed(1)}h</div>
          </div>
          <div className="rounded border p-4 bg-card" data-testid="total-lodestar-card">
            <div className="text-sm text-muted-foreground">Total Lodestar</div>
            <div className="text-2xl font-bold tabular-nums text-green-600">{formatCurrency(totalLodestar)}</div>
          </div>
          <div className="rounded border p-4 bg-card" data-testid="avg-rate-card">
            <div className="text-sm text-muted-foreground">Effective Rate</div>
            <div className="text-2xl font-bold tabular-nums">
              {totalHours > 0 ? formatCurrency(totalLodestar / totalHours) : "—"}/hr
            </div>
          </div>
        </div>
      )}

      {/* User Breakdown */}
      {!loading && userSummaries.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium">Timekeeper Breakdown</div>
          <div className="rounded border overflow-hidden">
            <table className="w-full text-sm" data-testid="lodestar-user-table">
              <thead className="bg-secondary/40">
                <tr>
                  <th className="text-left p-2">Timekeeper</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-right p-2">Rate</th>
                  <th className="text-right p-2">Hours</th>
                  <th className="text-right p-2">Lodestar</th>
                </tr>
              </thead>
              <tbody>
                {userSummaries.map(u => (
                  <tr key={u.userId} className="border-t">
                    <td className="p-2">{u.userName}</td>
                    <td className="p-2 text-muted-foreground">{u.userEmail}</td>
                    <td className="p-2 text-right tabular-nums">{u.rate ? formatCurrency(u.rate) : "—"}</td>
                    <td className="p-2 text-right tabular-nums">{u.totalHours.toFixed(1)}h</td>
                    <td className="p-2 text-right tabular-nums font-medium text-green-600">
                      {u.lodestar ? formatCurrency(u.lodestar) : "—"}
                    </td>
                  </tr>
                ))}
                {/* Total row */}
                <tr className="border-t-2 border-primary/20 bg-muted font-bold">
                  <td className="p-3" colSpan={3}>Total</td>
                  <td className="p-3 text-right tabular-nums">
                    {userSummaries.reduce((sum, u) => sum + u.totalHours, 0).toFixed(1)}h
                  </td>
                  <td className="p-3 text-right tabular-nums text-green-600">
                    {formatCurrency(userSummaries.reduce((sum, u) => sum + (u.lodestar || 0), 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Matter Breakdown */}
      {!loading && matterSummaries.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium">Matter Breakdown</div>
          <div className="rounded border overflow-hidden">
            <table className="w-full text-sm" data-testid="lodestar-matter-table">
              <thead className="bg-secondary/40">
                <tr>
                  <th className="text-left p-2">Matter</th>
                  <th className="text-right p-2">Hours</th>
                  <th className="text-right p-2">Lodestar</th>
                </tr>
              </thead>
              <tbody>
                {matterSummaries.map(m => (
                  <tr key={m.projectId || 'no-matter'} className="border-t">
                    <td className="p-2">{m.projectName || matterMap[m.projectId] || "(No matter)"}</td>
                    <td className="p-2 text-right tabular-nums">{m.totalHours.toFixed(1)}h</td>
                    <td className="p-2 text-right tabular-nums font-medium text-green-600">
                      {m.lodestar ? formatCurrency(m.lodestar) : "—"}
                    </td>
                  </tr>
                ))}
                {/* Total row */}
                <tr className="border-t-2 border-primary/20 bg-muted font-bold">
                  <td className="p-3">Total</td>
                  <td className="p-3 text-right tabular-nums">
                    {matterSummaries.reduce((sum, m) => sum + m.totalHours, 0).toFixed(1)}h
                  </td>
                  <td className="p-3 text-right tabular-nums text-green-600">
                    {formatCurrency(matterSummaries.reduce((sum, m) => sum + (m.lodestar || 0), 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No data message */}
      {!loading && (!analyticsData || (userSummaries.length === 0 && matterSummaries.length === 0)) && (
        <div className="text-center text-muted-foreground py-8" data-testid="lodestar-no-data">
          No data for the selected filters. Make sure users have hourly rates set in the Admin page.
        </div>
      )}
    </div>
  );
}
